import React, { useState, useEffect } from "react";
import { Cpu } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PROVIDER_LABELS = { openai: "OpenAI", gemini: "Gemini" };

export default function BusinessModelPanel({ business, onSaved }) {
  const [model, setModel] = useState(business.llm_model || "");
  const [defaultModel, setDefaultModel] = useState("");
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => { setModel(business.llm_model || ""); }, [business.id, business.llm_model]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    api.get("/ai/models").then(({ data }) => {
      if (cancelled) return;
      setModels(data.available_models || []);
      setDefaultModel(data.default_model || "");
    }).catch(() => {
      if (!cancelled) setLoadError(true);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [reloadCount]);

  const activeModel = model || defaultModel;
  const save = async (value) => {
    if (value === activeModel) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/businesses/${business.id}/llm-model`, { llm_model: value });
      setModel(data.llm_model);
      toast.success(`Model AI untuk ${business.name} tersimpan`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
      return;
    }
    try { if (onSaved) await onSaved(); }
    catch (e) { toast.error("Model tersimpan. Muat ulang halaman untuk memperbarui data bisnis."); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 rounded-xl border border-border bg-muted/30 p-4" data-testid="business-model-provider-card">
      <div className="flex items-center gap-2">
        <Cpu className="w-4 h-4 text-accent" />
        <h4 className="font-display font-semibold text-sm">Pilih Model Provider</h4>
      </div>
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        Pilih model AI untuk <b>{business.name}</b>. Pilihan ini hanya berlaku untuk bisnis ini.
        API key yang digunakan otomatis mengikuti provider model yang dipilih.
      </p>
      <Select value={activeModel} onValueChange={save} disabled={loading || busy || !models.length}>
        <SelectTrigger data-testid="select-business-llm-model" aria-label="Model AI bisnis">
          <SelectValue placeholder={loading ? "Memuat model..." : "Pilih model..."} />
        </SelectTrigger>
        <SelectContent>
          {models.map((item) => (
            <SelectItem key={item.id} value={item.id}>
              {PROVIDER_LABELS[item.provider] || item.provider} — {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {loadError ? (
        <div className="flex items-center justify-between gap-2 text-xs text-destructive" role="alert">
          <span>Daftar model gagal dimuat.</span>
          <Button type="button" variant="outline" size="sm" onClick={() => setReloadCount((value) => value + 1)}>
            Coba Lagi
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid="current-business-llm-model" aria-live="polite">
          {busy ? "Menyimpan model..." : "Model aktif: "}
          {!busy && <span className="font-medium text-foreground">{activeModel || "Memuat..."}</span>}
        </p>
      )}
    </div>
  );
}
